import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as documentService from '../../services/documents';
import { FiFileText, FiUsers, FiFolder, FiTrendingUp, FiClock, FiAlertCircle } from 'react-icons/fi';
import { MdDescription } from 'react-icons/md';
import './Dashboard.scss';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalDocuments: 0,
    assignedDocuments: 0,
    pendingDocuments: 0,
    recentDocuments: [],
  });

  const { data: documents, isLoading } = useQuery(
    'dashboard-documents',
    () => documentService.getDocuments({ limit: 10, sort: '-createdAt' }),
    {
      refetchInterval: 30000,
    }
  );

  useEffect(() => {
    if (documents?.documents) {
      setStats({
        totalDocuments: documents.total || 0,
        assignedDocuments: documents.documents.filter(doc => 
          doc.assignedTo?.some(a => a.user?._id === user?._id)
        ).length,
        pendingDocuments: documents.documents.filter(doc => doc.status === 'pending').length,
        recentDocuments: documents.documents.slice(0, 5),
      });
    }
  }, [documents, user]);

  const statCards = [
    {
      title: 'Total Documents',
      value: stats.totalDocuments,
      icon: <FiFileText />,
      color: '#0f6c11',
      link: '/documents',
    },
    {
      title: 'Assigned to Me',
      value: stats.assignedDocuments,
      icon: <FiUsers />,
      color: '#1a9b1d',
      link: '/documents?assigned=true',
    },
    {
      title: 'Pending Review',
      value: stats.pendingDocuments,
      icon: <FiClock />,
      color: '#F59E0B',
      link: '/documents?status=pending',
    },
    {
      title: 'Categories',
      value: '-',
      icon: <FiFolder />,
      color: '#2bc930',
      link: '/categories',
    },
  ];

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back, {user?.name || 'User'}!</p>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="stats-grid">
          {statCards.map((stat, index) => (
            <motion.div
              key={index}
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link to={stat.link} className="stat-link">
                <div className="stat-icon" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                  {stat.icon}
                </div>
                <div className="stat-info">
                  <h3>{stat.value}</h3>
                  <p>{stat.title}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="dashboard-sections">
          <motion.div
            className="section-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="section-header">
              <h2>Recent Documents</h2>
              <Link to="/documents" className="view-all">
                View All
              </Link>
            </div>
            <div className="section-content">
              {isLoading ? (
                <div className="loading-placeholder">
                  <div className="skeleton skeleton-text"></div>
                  <div className="skeleton skeleton-text"></div>
                  <div className="skeleton skeleton-text"></div>
                </div>
              ) : stats.recentDocuments.length > 0 ? (
                <div className="document-list">
                  {stats.recentDocuments.map((doc) => (
                    <Link
                      key={doc._id}
                      to={`/documents/${doc._id}`}
                      className="document-item"
                    >
                      <div className="document-icon">
                        <MdDescription />
                      </div>
                      <div className="document-info">
                        <h4>{doc.name}</h4>
                        <p className="document-meta">
                          {doc.documentNumber} • {new Date(doc.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className={`status-badge status-${doc.status}`}>
                        {doc.status}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FiFileText className="empty-icon" />
                  <p>No documents yet</p>
                  <Link to="/documents/create" className="btn btn-primary">
                    Create Document
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
